<template>
	<section class="bg-color-gray-80 text-color-white partners-panel pb-300 pb-600-md">
		<div class="container">
			<p class="body-xl" v-html="sectionDisplayText" />
			<h2 class="heading-5 mt-400 mb-50">Our banking and real estate partners</h2>
			<ul class="logo-grid">
				<li
					v-for="logo in partnerLogos"
					:key="logo.logo"
					class="partner-logo aspect-ratio-4-3"
					:style="`background-image: url(${logo.logo})`"
					:aria-label="logo.alt"
					role="img"
				></li>
			</ul>
		</div>
	</section>
</template>

<script setup lang="ts">
import { appCloudfrontPath } from '@constants';
import { computed } from 'vue';
import { deorphanText } from '@filters/shared-filters';
import { LogoImage } from 'types/marketing-site';

interface Props {
	sectionText?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
	sectionText: null
});

const defaultSectionText = `During this time, we have also worked with a majority of the top banks, sponsors, and capital
			brokers in the market, establishing a reputation as both a good partner and, importantly, as a
			partner who is not a "loan-to-own shop," seeking to prey on distress. As a result, we've become a
			sought-after capital source and developed a robust pipeline of deal flow tailored specifically to
			this type of opportunistic lending.`;

const partnerLogosData: Array<LogoImage> = [
	{
		logo: 'goldman-sachs-logo.png',
		alt: 'Goldman Sachs'
	},
	{
		logo: 'pgim-logo.png',
		alt: 'PGIM'
	},
	{
		logo: 'metlife-logo.png',
		alt: 'MetLife'
	},
	{
		logo: 'freddie-mac-logo.png',
		alt: 'Freddie Mac'
	},
	{
		logo: 'us-bank-logo.png',
		alt: 'US Bank'
	},
	{
		logo: 'capital-one-logo.png',
		alt: 'Capital One'
	},
	{
		logo: 'east-west-bank-logo-square.png',
		alt: 'East West bank'
	},
	{
		logo: 'synovus-logo.png',
		alt: 'Synovus'
	},
	{
		logo: 'truist-financial-logo.png',
		alt: 'Truist Financial'
	},
	{
		logo: 'citizens-bank-logo.png',
		alt: 'Citizens Bank'
	},
	{
		logo: 'key-bank-logo.png',
		alt: 'Key Bank'
	},
	{
		logo: 'regions-financial-logo.png',
		alt: 'Regions Financial'
	},

	{
		logo: 'himco-logo.png',
		alt: 'HIMCO'
	},
	{
		logo: 'allianz-logo.png',
		alt: 'Allianz'
	},
	{
		logo: 'pimco-logo.png',
		alt: 'PIMCO'
	}
];

const sectionDisplayText = computed(() => {
	return deorphanText(props.sectionText ?? defaultSectionText);
});

const partnerLogos = computed(() => {
	return partnerLogosData.map(({ logo, alt }) => {
		return {
			logo: `${appCloudfrontPath}/images/marketing-site/financial-partner-logos/${logo}`,
			alt
		};
	});
});
</script>

<style lang="scss">
@use '../../../styles/constants/colors' as *;
@use '../../../styles/utilities/respond-to.scss' as *;

.partners-panel {
	@include respond-to(smo) {
		margin-top: -1px;
		margin-bottom: -1px;
	}

	.body-xl {
		max-width: 720px;
	}

	.logo-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 1.5rem;

		@include respond-to(md) {
			grid-template-columns: repeat(5, 1fr);
		}
	}

	.partner-logo {
		background-position: center;
		background-repeat: no-repeat;
		background-size: 75% auto;
		filter: opacity(0.6);

		&:last-child {
			@include respond-to(smo) {
				grid-column: span 2;
				margin-left: auto;
				margin-right: auto;
				padding-bottom: calc(1 / 3) * 100%;
				width: 250px;
			}
		}
	}
}
</style>
